import { inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { type Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import {
  catchError,
  filter,
  first,
  forkJoin,
  map,
  type Observable,
  of,
  switchMap,
  tap,
  timeout,
} from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementType,
  type PartialPropsOf,
  setWrapperRefMutably,
  WrapperCommitButton,
  WrapperField,
} from '@blaze-runtime/engine-domain';
import { type ColumnId } from '@blaze-shared/api-contracts-domain';
import {
  type CanvasElementDataProperties,
  type DataBindingSourceInfo,
  findDisplayColumnOrGetSingle,
  findIdColumnKey,
  findWorkflowGetSourceNode,
  getActiveBinding,
  getOptionsListBindingForTagColumn,
  type PropsUpdate,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  type BlazeTableSchema,
  findColumn,
  findColumnKey,
  getColumnKey,
} from '@blaze-shared/schema-domain';
import { type FormActionType } from '@blaze-shared/workflow-domain';

import { fromElements } from '../../../+state';
import { ElementsFacade } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import { RuntimeFacade } from '../../../+state/runtime/runtime.facade';
import {
  type AiChildGroup,
  AiWorkflowGenerationService,
  type BaseAiElementDefinition,
  bindValueToWorkflowSource,
  type ContainerVisibleField,
  createElement,
  dispatchValueBindingRefresh,
  type ElementHandler,
  ElementHandlerResolver,
  IconResolverService,
  type LinkDisplayProperties,
} from '../../../ai';
import {
  filterOutInternalColumns,
  getColumnsFromSchema,
} from '../../../canvas-elements/element-model/data-binding';
import { hasDataProps } from '../../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import {
  type GenerationElementType,
  getFormFieldsDataTypeMapping,
} from '../../../canvas-elements/element-model/form-fields-generation';
import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';
import { ElementDataSourceService } from '../../../services/element-data-source.service';
import { FormSubmitWorkflowService } from '../../../services/workflow-generation/public-api';
import { buildFormInputValueBinding, getFieldData } from '../../../utils/elements/generation.utils';
import { type ButtonSpecialPurpose } from '../../button/ai/button.definition';

import { type FormContainerDefinition } from './form-container.definition';
import { type FormDetailsDefinition } from './form-details.definition';
import { type FormContext, type FormResolvedContext } from './form.definition';

const FORM_VISIBLE_FIELDS_REFRESH_TIMEOUT_MS = 10_000;
const FORM_VALUE_BINDING_REFRESH_TIMEOUT_MS = 10_000;

interface CustomLayoutFieldBinding {
  childId: string;
  field: string;
  displayProperties?: LinkDisplayProperties;
}

interface CustomLayoutTargets {
  fieldBindings: CustomLayoutFieldBinding[];
  commitButtonChildId: string | undefined;
}

export abstract class FormElementHandler<
  T extends Extract<CanvasElementType, 'formDetails' | 'formContainer'>,
> implements ElementHandler<T>
{
  private readonly aiWorkflowGenerationService = inject(AiWorkflowGenerationService);
  private readonly handlerResolver = inject(ElementHandlerResolver);
  protected readonly actions$ = inject(Actions);
  protected readonly elementsFacade = inject(ElementsFacade);
  protected readonly runtimeFacade = inject(RuntimeFacade);
  protected readonly iconResolverService = inject(IconResolverService);
  protected readonly store = inject(Store);
  protected readonly schemaService = inject(SchemaService);
  protected readonly elementDataSourceService = inject(ElementDataSourceService);
  protected readonly workflowGenerationUtilsService = inject(FormSubmitWorkflowService);

  abstract readonly type: T;

  getChildGroups(element: BaseAiElementDefinition<T>): AiChildGroup[] {
    const children = this.getCustomLayoutChildren(element);
    return children ? [{ slotId: undefined, children }] : [];
  }

  prepare(context: FormContext<T>): Observable<FormResolvedContext<T>> {
    if (context.element.iconName) {
      context.element.iconName = this.iconResolverService.resolveFullIconName(
        context.element.iconName,
      );
    }

    const layout = (context.element as FormContainerDefinition).layout;
    if (layout?.$type === 'auto' && layout.submitIconName) {
      layout.submitIconName = this.iconResolverService.resolveFullIconName(layout.submitIconName);
    }

    return of(context);
  }

  process(context: FormResolvedContext<T>, draft: WritableDraft<EngineState>): void {
    const props = this.getFormProps(context);
    const result = createElement(draft, context, props);

    const element = draft.entities[result?.elementId ?? ''];

    if (!isElementType(element, this.type)) {
      return;
    }

    const workflowContext = context.componentWorkflows[context.element.id];
    if (workflowContext) {
      const workflow = workflowContext.workflow;
      bindValueToWorkflowSource(draft, context, workflow);
      context.workflowsToFinalize = [{ workflow }];
    }

    element.height = {
      mode: 'value',
      value: 100,
      fixed: false,
    };

    this.registerActionWorkflows(context, draft);
  }

  finalize(element: CanvasElementFrom<T>, context: FormResolvedContext<T>): Observable<any> {
    const targets = this.collectCustomLayoutTargets(context.element);
    this.warnOnDeadCustomLayoutForm(element.id, targets);
    const commitButtonId = targets.commitButtonChildId
      ? (context.idsMapping[targets.commitButtonChildId] ?? targets.commitButtonChildId)
      : undefined;
    return this.getDataBindingSourceInfo(element, context, context.element.tableName).pipe(
      switchMap((bindingSourceInfo) => {
        // custom-layout field refs must land before `refreshBinding` dispatches `updateBinding`;
        // without the refs in place first, the dashboard's form-regeneration effect, wouldn't
        // recognize the AI-placed fields
        const finalizeWorkflows$ = forkJoin([
          this.aiWorkflowGenerationService.handleAiGeneratedWorkflows(
            context.workflowsToFinalize ?? [],
          ),
          this.setFormWrapperRefs(
            element,
            context,
            bindingSourceInfo,
            targets.fieldBindings,
            commitButtonId,
          ),
          this.setFieldDataBindings(element.id, context, bindingSourceInfo, targets.fieldBindings),
        ]).pipe(switchMap(() => this.refreshBinding(element.id)));

        const submitWorkflow$ = this.createOrUpdateSubmitWorkflow(
          element,
          context,
          bindingSourceInfo,
          commitButtonId,
        );

        if (
          bindingSourceInfo?.sourceType !== 'dataset' &&
          bindingSourceInfo?.sourceType !== 'context'
        ) {
          return forkJoin([finalizeWorkflows$, submitWorkflow$]);
        }

        const visibleFields = this.getVisibleFields(context);
        const submitButtonOverride = this.getSubmitButtonTitleOverrides(context);
        const isCustomLayout = context.element.layout?.$type === 'custom';
        const formRegenerated$ =
          visibleFields?.length || submitButtonOverride || isCustomLayout
            ? this.waitForFormRegenerated(element.id)
            : of(undefined);

        const setDataSource$ =
          bindingSourceInfo.sourceType === 'dataset'
            ? this.elementDataSourceService.setDataSourceToDataset({
                bindingKey: 'value',
                dataProps: [],
                selectedDataset: bindingSourceInfo,
                isNewDataSource: true,
                mode: 'form-action',
                elementId: element.id,
                projectId: context.projectId,
                setDynamicSearchIfBigTable: false,
                rawOptionsWorkflows: [],
                updateKnobPropsFn: () => {
                  /* EMPTY */
                },
                elementType: element.type,
                previousDataSourceId: undefined,
              })
            : of(null);

        return forkJoin([
          formRegenerated$,
          finalizeWorkflows$,
          setDataSource$,
          submitWorkflow$,
        ]).pipe(
          switchMap(([regeneratedCommitButtonId]) => {
            const followUps: Observable<unknown>[] = [];
            if (visibleFields?.length) {
              followUps.push(this.updateModelVisibility(element.id, visibleFields));
            }
            if (isCustomLayout) {
              followUps.push(
                this.updateCustomLayoutFieldVisibilityAndControlTypes(
                  context,
                  element.id,
                  bindingSourceInfo,
                  targets.fieldBindings,
                ),
              );
            }
            if (submitButtonOverride && regeneratedCommitButtonId) {
              followUps.push(
                this.applySubmitButtonOverride(regeneratedCommitButtonId, submitButtonOverride),
              );
            }
            return followUps.length ? forkJoin(followUps) : of(undefined);
          }),
        );
      }),
    );
  }

  protected abstract getFormProps(context: FormResolvedContext<T>): PartialPropsOf<T>;

  protected getVisibleFields(context: FormResolvedContext<T>): ContainerVisibleField[] | undefined {
    return context.element.layout?.$type === 'auto'
      ? context.element.layout.visibleFields
      : undefined;
  }

  protected getColumnCount(context: FormResolvedContext<T>): number | undefined {
    return context.element.layout?.$type === 'auto'
      ? context.element.layout.columnCount
      : undefined;
  }

  protected abstract getSubmitButtonTitleOverrides(
    context: FormResolvedContext<T>,
  ): { label?: string; iconName?: string } | undefined;

  /**
   * The submit action for a `SubmitsForm` commit button;
   */
  protected abstract getFormActionType(context: FormResolvedContext<T>): FormActionType | undefined;

  /**
   * Registers any element-level custom-action workflows into the draft and queues them for finalization.
   */
  protected registerActionWorkflows(
    _context: FormResolvedContext<T>,
    _draft: WritableDraft<EngineState>,
  ): void {
    /* overridden in FormDetailsElementHandler */
  }

  private getCustomLayoutChildren(
    element: BaseAiElementDefinition<CanvasElementType>,
  ): BaseAiElementDefinition<CanvasElementType>[] | undefined {
    const layout = (element as FormContainerDefinition | FormDetailsDefinition).layout;
    return layout?.$type === 'custom' ? layout.children : undefined;
  }

  private collectCustomLayoutTargets(
    formElement: BaseAiElementDefinition<CanvasElementType>,
  ): CustomLayoutTargets {
    const fieldBindings: CustomLayoutFieldBinding[] = [];
    let commitButtonChildId: CanvasElementId | undefined;

    const visit = (element: BaseAiElementDefinition<CanvasElementType>): void => {
      if (element.$type === 'formContainer' || element.$type === 'formDetails') {
        return;
      }

      const { field, displayProperties, specialPurpose } = element as {
        field?: string;
        displayProperties?: LinkDisplayProperties;
        specialPurpose?: ButtonSpecialPurpose;
      };

      if (field) {
        fieldBindings.push({ childId: element.id, field, displayProperties });
      }

      if (element.$type === 'button' && specialPurpose === 'SubmitsForm') {
        if (commitButtonChildId) {
          console.warn(
            `Form (id:${formElement.id}): multiple SubmitsForm buttons found; using the first`,
          );
        } else {
          commitButtonChildId = element.id;
        }
      }

      for (const child of this.getChildElements(element)) {
        visit(child);
      }
    };

    for (const child of this.getCustomLayoutChildren(formElement) ?? []) {
      visit(child);
    }

    return { fieldBindings, commitButtonChildId };
  }

  private getChildElements(
    element: BaseAiElementDefinition<CanvasElementType>,
  ): BaseAiElementDefinition<CanvasElementType>[] {
    const groups = this.handlerResolver.resolve(element.$type)?.getChildGroups?.(element) ?? [];
    return groups.flatMap((group) => group.children);
  }

  private warnOnDeadCustomLayoutForm(elementId: string, targets: CustomLayoutTargets): void {
    if (targets.fieldBindings.length && !targets.commitButtonChildId) {
      console.warn(
        `Form (id:${elementId}): custom-layout form has field inputs but no SubmitsForm button`,
      );
    }
  }

  private createOrUpdateSubmitWorkflow(
    element: CanvasElementFrom<T>,
    context: FormResolvedContext<T>,
    bindingSourceInfo: DataBindingSourceInfo | null,
    commitButtonId: CanvasElementId | undefined,
  ): Observable<void> {
    const formActionType = this.getFormActionType(context);

    if (!formActionType || !commitButtonId || !bindingSourceInfo) {
      return of(undefined);
    }

    // prop available only on `formContainer`.
    const upsert = isElementType(element, 'formContainer')
      ? element.props.content.options?.addIfMissing
      : undefined;

    return this.workflowGenerationUtilsService
      .createOrUpdateSubmitWorkflowAndConnections(
        context.projectId,
        element.ns,
        element.id,
        commitButtonId,
        formActionType,
        bindingSourceInfo,
        { isDialog: false, upsert },
      )
      .pipe(
        tap((result) => {
          if (!result) {
            return;
          }
          this.store.dispatch(
            runtimeActions.prefilledPageLayoutGenerated({
              workflow: result.workflow,
              connections: [result.formConnection, result.buttonConnection],
            }),
          );
        }),
        map(() => undefined),
      );
  }

  private setFormWrapperRefs(
    element: CanvasElementFrom<T>,
    context: FormResolvedContext<T>,
    bindingSourceInfo: DataBindingSourceInfo | null,
    fieldBindings: CustomLayoutFieldBinding[],
    commitButtonId: CanvasElementId | undefined,
  ): Observable<void> {
    const canWriteFieldRefs = fieldBindings.length > 0 && !!bindingSourceInfo;

    if (!canWriteFieldRefs && !commitButtonId) {
      return of(undefined);
    }

    return this.elementsFacade.entities$.pipe(
      first(),
      tap((entities) => {
        const formEntity = entities[element.id];
        if (!isElementType(formEntity, this.type)) {
          return;
        }

        const refs = { ...getWrapperRefs(formEntity) };

        if (bindingSourceInfo) {
          for (const { childId, field } of fieldBindings) {
            const column = findColumn(bindingSourceInfo.columns, field);
            if (!column) {
              console.warn(`Form (id:${element.id}): field "${field}" matches no column`);
              continue;
            }
            const childElementId = context.idsMapping[childId] ?? childId;
            setWrapperRefMutably(
              refs,
              WrapperField.getRefIds(column.name, column.id),
              childElementId,
            );
          }
        }

        if (commitButtonId) {
          setWrapperRefMutably(refs, WrapperCommitButton.getRefIds(), commitButtonId);
        }

        this.store.dispatch(
          fromElements.updateElementProps({
            elementId: element.id,
            path: 'wrapper.refs',
            value: refs,
          }),
        );
      }),
      map(() => undefined),
    );
  }

  private setFieldDataBindings(
    formElementId: CanvasElementId,
    context: FormResolvedContext<T>,
    bindingSourceInfo: DataBindingSourceInfo | null,
    fieldBindings: CustomLayoutFieldBinding[],
  ): Observable<void> {
    if (!bindingSourceInfo || !fieldBindings.length) {
      return of(undefined);
    }

    return this.elementsFacade.entities$.pipe(
      first(),
      switchMap((entities) =>
        forkJoin(
          fieldBindings.map((binding) =>
            this.setChildDataBindings(formElementId, context, bindingSourceInfo, binding, entities),
          ),
        ),
      ),
      map(() => undefined),
    );
  }

  private setChildDataBindings(
    formElementId: CanvasElementId,
    context: FormResolvedContext<T>,
    bindingSourceInfo: DataBindingSourceInfo,
    binding: CustomLayoutFieldBinding,
    entities: Dictionary<CanvasElement>,
  ): Observable<void> {
    const column = findColumn(bindingSourceInfo.columns, binding.field);
    const childElementId = context.idsMapping[binding.childId] ?? binding.childId;
    const child = entities[childElementId];

    if (!column || !child) {
      return of(undefined);
    }

    const readInBinding = buildFormInputValueBinding(
      formElementId,
      column,
      child.type,
      'formContainer',
    );
    const optionsBinding$: Observable<CanvasElementDataProperties | undefined> =
      column.dataType === 'Link' && column.link?.tableId
        ? this.getLinkOptionsListBinding(column.link.tableId, binding.displayProperties)
        : column.dataType === 'Tag'
          ? of(getOptionsListBindingForTagColumn(bindingSourceInfo.id, getColumnKey(column)))
          : of(undefined);

    return optionsBinding$.pipe(
      tap((optionsBinding) => {
        const data: CanvasElementDataProperties[] = [
          ...(hasDataProps(child)
            ? child.props.data.filter(
                (existing) =>
                  existing?.key !== readInBinding.key && existing?.key !== 'optionsList',
              )
            : []),
          readInBinding,
          ...(optionsBinding ? [optionsBinding] : []),
        ];
        this.store.dispatch(
          fromElements.updateElementProps({ elementId: childElementId, path: 'data', value: data }),
        );
      }),
      map(() => undefined),
    );
  }

  private getLinkOptionsListBinding(
    tableId: string,
    displayProperties: LinkDisplayProperties | undefined,
  ): Observable<CanvasElementDataProperties | undefined> {
    return this.schemaService.obtainSchema<BlazeTableSchema>(tableId).pipe(
      first(),
      map((linkedSchema) => {
        if (!linkedSchema) {
          return undefined;
        }

        const valueColumn = findIdColumnKey(linkedSchema);
        const labelColumn = displayProperties?.display
          ? findColumnKey(linkedSchema.columns, displayProperties.display) ||
            displayProperties.display
          : findDisplayColumnOrGetSingle(linkedSchema);

        return {
          key: 'optionsList',
          enabled: true,
          dataSource: {
            ...linkedSchema,
            sourceType: 'dataset',
            projectColumns: true,
          } as DataBindingSourceInfo,
          bindings: { value: valueColumn, label: labelColumn },
        };
      }),
    );
  }

  private updateCustomLayoutFieldVisibilityAndControlTypes(
    context: FormResolvedContext<T>,
    elementId: CanvasElementId,
    bindingSourceInfo: DataBindingSourceInfo,
    fieldBindings: CustomLayoutFieldBinding[],
  ): Observable<void> {
    return this.elementsFacade.entities$.pipe(
      first(),
      tap((entities) => {
        const element = entities[elementId];
        if (!isElementType(element, this.type) || !element.props.content.model?.length) {
          return;
        }

        const pregenerationElementsMap = getFormFieldsDataTypeMapping(
          this.getFormActionType(context) === 'view',
        );
        const placedColumnIds = new Set<ColumnId>();
        const placedControlTypeByColumnId = new Map<ColumnId, GenerationElementType>();

        for (const { childId, field } of fieldBindings) {
          const column = findColumn(bindingSourceInfo.columns, field);
          if (!column?.id) {
            continue;
          }
          placedColumnIds.add(column.id);
          const childElementId = context.idsMapping[childId] ?? childId;
          const controlType = getFieldData(
            column,
            pregenerationElementsMap,
            entities[childElementId]?.type,
          ).controlType;
          if (controlType) {
            placedControlTypeByColumnId.set(column.id, controlType);
          }
        }

        const model = element.props.content.model.map((modelField) => {
          const isPlaced = !!modelField.fieldId && placedColumnIds.has(modelField.fieldId);

          if (isPlaced) {
            const controlType =
              modelField.fieldId && placedControlTypeByColumnId.get(modelField.fieldId);
            return {
              ...modelField,
              visible: true,
              controlType: controlType ?? modelField.controlType,
            };
          }

          if (modelField.controlType) {
            return { ...modelField, visible: false };
          }

          const column = findColumn(bindingSourceInfo.columns, {
            id: modelField.fieldId,
            name: modelField.field,
          });
          const controlType = column
            ? getFieldData(column, pregenerationElementsMap, undefined).controlType
            : undefined;

          return { ...modelField, visible: false, controlType };
        });

        this.store.dispatch(
          fromElements.updateElementProps({ elementId, path: 'content.model', value: model }),
        );
      }),
      map(() => undefined),
    );
  }

  private getDataBindingSourceInfo(
    element: CanvasElementFrom<T>,
    context: FormResolvedContext<T>,
    tableName: string | undefined,
  ): Observable<DataBindingSourceInfo | null> {
    const valueBinding = getActiveBinding(element.props.data, 'value');
    if (valueBinding?.dataSource?.sourceType === 'context') {
      return this.resolveContextSourceColumns(context, valueBinding.dataSource);
    }
    if (!tableName) {
      return of(null);
    }
    return this.schemaService.getAllDatasets(context.projectId).pipe(
      map((datasets) => {
        const datasetId = context.datasetNameToId[tableName];
        const dataset = datasets.find((dataset) => dataset.id === datasetId);

        if (!dataset) {
          return null;
        }

        return <DataBindingSourceInfo>{
          ...dataset,
          connectorName: dataset.projectName || dataset.connectorName,
          sourceType: 'dataset',
          columns: filterOutInternalColumns(getColumnsFromSchema(dataset)),
        };
      }),
    );
  }

  /**
   * A context-backed `value` binding is written during `process()` with whatever schema is
   * already loaded in the draft (see `bindValueToWorkflowSource`), which is often empty for a
   * freshly-imported source workflow. Re-resolve columns here from the same source workflow's
   * Get-node dataset, falling back to the binding as-is (empty columns) when none resolves.
   */
  private resolveContextSourceColumns(
    context: FormResolvedContext<T>,
    contextSource: DataBindingSourceInfo,
  ): Observable<DataBindingSourceInfo> {
    const sourceWorkflow = context.componentWorkflows[context.element.id]?.workflow;
    const datasetId = findWorkflowGetSourceNode(sourceWorkflow)?.datasetId;

    if (!datasetId) {
      return of(contextSource);
    }

    return this.schemaService.getAllDatasets(context.projectId).pipe(
      map((datasets) => {
        const dataset = datasets.find((dataset) => dataset.id === datasetId);
        return dataset
          ? { ...contextSource, columns: filterOutInternalColumns(getColumnsFromSchema(dataset)) }
          : contextSource;
      }),
    );
  }

  private waitForFormRegenerated(
    elementId: CanvasElementId,
  ): Observable<CanvasElementId | undefined> {
    return this.actions$.pipe(
      ofType(fromElements.formRegenerated),
      filter((action) => action.formId === elementId),
      first(),
      map((action) => action.commitButtonId),
      timeout({
        first: FORM_VISIBLE_FIELDS_REFRESH_TIMEOUT_MS,
        with: () => {
          console.warn(`Form (id:${elementId}): form regeneration did not complete in time`);
          return of(undefined);
        },
      }),
      catchError((error) => {
        console.warn(`Form (id:${elementId}): failed to observe form regeneration`, error);
        return of(undefined);
      }),
    );
  }

  private applySubmitButtonOverride(
    commitButtonId: CanvasElementId,
    override: { label?: string; iconName?: string },
  ): Observable<void> {
    const updates: PropsUpdate[] = [];
    if (override.label) {
      updates.push({ path: 'content.button.label', value: override.label });
    }
    if (override.iconName) {
      updates.push({ path: 'content.button.icon', value: override.iconName });
    }
    if (updates.length) {
      this.store.dispatch(
        fromElements.updateElementPropsBatch({ elementId: commitButtonId, updates }),
      );
    }
    return of(undefined);
  }

  private updateModelVisibility(
    elementId: CanvasElementId,
    visibleFields: ContainerVisibleField[],
  ): Observable<void> {
    return this.elementsFacade.entities$.pipe(
      first(),
      tap((entities) => {
        const element = entities[elementId];

        if (!isElementType(element, this.type) || !element.props.content.model?.length) {
          return;
        }

        const model = element.props.content.model.map((field) => ({
          ...field,
          visible: visibleFields.some((visibleField) =>
            findColumn([{ id: field.fieldId, name: field.field }], visibleField.field),
          ),
        }));

        this.store.dispatch(
          fromElements.updateElementProps({ elementId, path: 'content.model', value: model }),
        );
      }),
      map(() => undefined),
    );
  }

  private refreshBinding(elementId: CanvasElementId): Observable<void> {
    return dispatchValueBindingRefresh({
      elementsFacade: this.elementsFacade,
      runtimeFacade: this.runtimeFacade,
      store: this.store,
      elementId,
      timeoutMs: FORM_VALUE_BINDING_REFRESH_TIMEOUT_MS,
      onTimeout: () => console.warn(`Form ${elementId}: value binding schema did not resolve`),
      onError: (error) => console.warn(`Form ${elementId}: refresh binding failed`, error),
    });
  }
}
